import { gql, useMutation } from '@apollo/client';
import { useRef, useState, useEffect } from "react";
import { Column, createRecord, insertRecordBySeq, ResultTable, ScanRecord } from "./components/ResultTable";
import { Pda } from "./components/Pda";
import { parseException, showError, showRepeatedScanError, parseExceptionMessageKey, clearCurrentError } from "utils/newExceptionUtils";
import { ScanInput } from "./components/ScanInput";
import { Button, Input, Toast } from "antd-mobile";
import { ScanningOutline, CloseCircleFill } from "antd-mobile-icons";
import { Header } from './components/Header';
import {isBagNumber, isTrackingNumber, showBarCodeError} from '../../utils/BarCodeUtil';
import { useLazyQuery } from '@apollo/client';
import { GET_BAG_BASIC_INFO_BY_NUMBER, GetBagBasicInfoByNumberResponse, GetBagBasicInfoByNumberVariables } from '../../graphql/queries/bagQueries';
import { playScanFailedAoSound, playRepeatedScanSound } from "../../utils/scanUtils";
import { t } from '../../i18n';
import { useScanQueueConsumer } from "./components/useScanQueueConsumer";
import { PdaScanEvent } from "./components/Pda";

// BaggingPage专用的错误Toast，支持关闭回调
let currentBaggingErrorToast: any = null;

const showBaggingError = (error: any, onClose?: () => void) => {
  // 先清除之前的Toast
  if (currentBaggingErrorToast) {
    currentBaggingErrorToast.close();
    currentBaggingErrorToast = null;
  }

  const { title, message } = parseException(error);
  
  currentBaggingErrorToast = Toast.show({
    content: (
      <div className="relative py-2" style={{ minWidth: '200px', maxWidth: '80vw' }}>
        <div className="flex items-center justify-center mb-2 relative">
          <div className="flex items-center gap-2">
            <CloseCircleFill fontSize={20} color='var(--adm-color-danger)'/>
            <div className="text-base">{title}</div>
          </div>
          <div
            className="absolute right-0 cursor-pointer flex items-center justify-center w-6 h-6"
            onClick={() => {
              if (currentBaggingErrorToast) {
                currentBaggingErrorToast.close();
                currentBaggingErrorToast = null;
              }
              onClose?.();
            }}
          >
            <span className="text-white text-lg">✕</span>
          </div>
        </div>
        <div className="text-sm text-center px-4">{message}</div>
      </div>
    ),
    duration: 0 // 常驻直到关闭
  });
};

const clearBaggingError = () => {
  if (currentBaggingErrorToast) {
    currentBaggingErrorToast.close();
    currentBaggingErrorToast = null;
  }
};


export interface HandleScanEventResult {
  success: boolean;
}

const HANDLE_WHS_BAGGING_SCAN_EVENT = gql`
mutation HandleWhsBaggingScanEvent($bagNumber: String!, $trackingNumber: String!) {
    success: handleWhsBaggingScanEvent(bagNumber: $bagNumber, trackingNumber: $trackingNumber)
}
`

const PageBody = () => {

  const [handleWhsBaggingScanEvent] = useMutation<HandleScanEventResult>(HANDLE_WHS_BAGGING_SCAN_EVENT);
  const [getBagBasicInfo] = useLazyQuery<GetBagBasicInfoByNumberResponse, GetBagBasicInfoByNumberVariables>(GET_BAG_BASIC_INFO_BY_NUMBER);

  const handleScanEvent = async (value1: string, value2: string): Promise<HandleScanEventResult> => {
    const { data } = await handleWhsBaggingScanEvent({ variables: { bagNumber: value1, trackingNumber: value2 } });
    return { success: data?.success || false };
  };

  const [successRecords, setSuccessRecords] = useState<ScanRecord[]>([]);
  const [failedRecords, setFailedRecords] = useState<ScanRecord[]>([]);
  const [bagNumber, setBagNumber] = useState('');
  const [trackingNumber, setTrackingNumber] = useState('');
  const [frameColor, setFrameColor] = useState<'red' | null>(null);

  // ref to read latest bagNumber inside async onProcess callback
  const bagNumberRef = useRef(bagNumber);
  bagNumberRef.current = bagNumber;

  const clearFrame = () => setFrameColor(null);

  // 组件卸载时清理错误弹窗
  useEffect(() => {
    return () => {
      clearCurrentError();
      clearBaggingError();
    };
  }, []);


  const onProcess = async (event: PdaScanEvent) => {
    const barCode = event.code;

    // PDA 硬件扫描绕过 ScanInput.onScan 直接入队，包牌/运单号分流必须在此统一处理
    if (isBagNumber(barCode)) {
      // 清除之前的错误状态
      clearCurrentError();
      clearBaggingError();
      setFrameColor(null);
      setBagNumber(barCode);
      return;
    }
    // 不是已知格式的包牌号，尝试查后端判断是否为外部包牌号
    try {
      const bagQueryResult = await getBagBasicInfo({ variables: { bagNumber: barCode } });
      if (bagQueryResult.data?.getBagBasicInfoByNumber) {
        // 清除之前的错误状态
        clearCurrentError();
        clearBaggingError();
        setFrameColor(null);
        setBagNumber(barCode);
        return;
      }
    } catch {
      // 查询失败则继续走运单号逻辑
    }
    if (!isTrackingNumber(barCode)) {
      playScanFailedAoSound();
      setFrameColor('red');
      showBaggingError({
        title: t('pda.errors.invalidBarCode.title'),
        message: t('pda.errors.invalidBarCode.message', { barCode }),
      }, clearFrame);
      return;
    }

    // 兼容收起键盘
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }

    const currentBagNumber = bagNumberRef.current;
    if (currentBagNumber === '') {
      playScanFailedAoSound();
      setFrameColor('red');
      showBaggingError({ title: t('pda.common.error'), message: t('pda.common.bagCodeRequired') }, clearFrame);
      return;
    }

    setTrackingNumber(barCode);

    // 清除之前的错误
    clearCurrentError();
    clearBaggingError();
    setFrameColor(null);

    try {
      const { success } = await handleScanEvent(currentBagNumber, barCode);
      if (success) {
        setSuccessRecords(prev => insertRecordBySeq(prev, createRecord({ value1: currentBagNumber, value2: barCode, seq: event.seq })));
      }
    } catch (error) {
      const messageKey = parseExceptionMessageKey(error);
      let message: string;
      if (messageKey === "errors.business.alreadyScanned") {
        playRepeatedScanSound();
        setFrameColor('red');
        showBaggingError(error, clearFrame);
        const { message: errorMessage } = parseException(error);
        message = errorMessage;
      } else {
        const { message: errorMessage } = parseException(error);
        playScanFailedAoSound();
        setFrameColor('red');
        showBaggingError(error, clearFrame);
        message = errorMessage;
      }
      setFailedRecords(prev => insertRecordBySeq(prev, createRecord({ value1: currentBagNumber, value2: barCode, failedReason: message, seq: event.seq })));
    }
  };

  const { enqueueScan } = useScanQueueConsumer({ maxConcurrency: 1, onProcess });

  const successColumns: Column[] = [
    { title: t('pda.pages.bagging.bagCode'), key: 'value1' },
    { title: t('pda.pages.bagging.trackingNO.'), key: 'value2' },
    { title: t('pda.common.time'), key: 'time' }
  ];

  const failedColumns = [
    ...successColumns,
    { title: t('pda.common.failedReason'), key: 'failedReason' }
  ];

  const handleBagCodeChange = (value: string) => {
      setBagNumber(value);
  };

  const frameStyle = frameColor === 'red' ? { borderColor: '#c7381e' } : undefined;

  return (
    <div className="flex flex-col gap-2">
      {/* 红色边框覆盖层 */}
      {frameColor && (
        <div className={`fixed inset-0 border-8 pointer-events-none z-[1100] ${frameColor === 'red' ? '' : 'border-red-500'}`} style={frameStyle} />
      )}

      <div className="flex items-center mb-2 border rounded">
        <Input
          className="flex-1 p-2"
          style={{ fontSize: '10px' }}
          placeholder={t('pda.pages.bagging.bagCode')}
          value={bagNumber}
          onChange={handleBagCodeChange}
        />
        <Button
          fill={"none"}
          className="ml-2 bg-transparent border-0"
        >
          <ScanningOutline fontSize={24} />
        </Button>
      </div>
      <ScanInput placeholder={t('pda.pages.bagging.trackingNO.')} value={trackingNumber} onScan={enqueueScan} />

      <ResultTable
        successRecords={successRecords}
        failedRecords={failedRecords}
        successColumns={successColumns}
        failedColumns={failedColumns}
      />
    </div>
  );

}

export default function BaggingPage() {
  return (
    <div className="w-full h-full flex flex-col gap-2 p-2">
      <Header title={t('pda.pages.bagging.baggingScan')} />
      <Pda />
      <PageBody />
    </div>
  );
}